import type { FacebookLoginConfig } from '../api/types';
import { setupFacebookSdk, type FbLoginOptions } from './whatsappSignup';

/**
 * The Facebook dialog for PAGES — "Continue with Facebook" on the Messenger and
 * Instagram connect screens. Unlike the WhatsApp embedded signup there is no
 * second half to wait for: `FB.login` resolves a short-lived USER access token,
 * and everything else (the Page list, the token exchange, the Page's own token)
 * happens on the server against that.
 *
 * Which dialog opens is the deployment's choice: a Facebook Login for Business
 * configuration when one is configured (a Business-type Meta app only offers
 * that), the scope list otherwise (classic Facebook Login).
 *
 * Load the SDK when the screen opens (`setupFacebookSdk`), not on the click —
 * FB.login opens a popup and needs the click's transient activation, which a
 * cold script load outlives.
 */
export interface FacebookLoginDone {
  status: 'done';
  userAccessToken: string;
}

export interface FacebookLoginCancelled {
  status: 'cancelled';
}

export type FacebookLoginOutcome = FacebookLoginDone | FacebookLoginCancelled;

export const loginOptions = (config: FacebookLoginConfig): FbLoginOptions =>
  config.configurationId ? { config_id: config.configurationId } : { scope: config.scopes.join(',') };

export const runFacebookLogin = async (config: FacebookLoginConfig): Promise<FacebookLoginOutcome> => {
  if (!config.available || !config.appId) throw new Error('Facebook sign-in is not configured.');
  await setupFacebookSdk(config);
  return new Promise((resolve, reject) => {
    if (!window.FB) {
      reject(new Error('The Facebook SDK is not ready — try again.'));
      return;
    }
    // FB.login never rejects: a closed popup, a declined dialog and an unknown
    // status all come back as a response with no token, which is a cancel.
    window.FB.login((response) => {
      const token = response.status === 'connected' ? response.authResponse?.accessToken : null;
      if (token) resolve({ status: 'done', userAccessToken: token });
      else if (response.error) reject(new Error(response.error));
      else resolve({ status: 'cancelled' });
    }, loginOptions(config));
  });
};
